import { useCallback, useMemo } from 'react';

import { Button } from '@/components/form/baseInputs/Button';
import { useNavigateToRoute } from '@/components/navigation/useNavigateToRoute';
import { TableEmptyState } from '@/components/tables/DataTable/components/TableEmptyState';
import { DataTable } from '@/components/tables/DataTable/DataTable';
import { ActionButtonRenderer } from '@/components/tables/DataTable/renderers/cell/ActionButtonRenderer';
import { Column } from '@/components/tables/DataTable/types';
import { useReportError } from '@/hooks/useReportError';
import { ROUTE_KEYS } from '@/navigation/constants';
import { PresentationTemplateType } from '@/types/schema';

import { NEW_TEMPLATE_SENTINEL } from '../PresentationTemplateEditor/PresentationTemplateEditor.constants';
import { createPinnedPresentationTemplateColumn } from '../presentationTemplateTable.columns';
import {
  PresentationTemplateTableVariant,
  presentationTemplateTypeByTableVariant,
} from '../presentationTemplateTable.types';
import { PresentationTemplatePublishingMenuItem } from './PresentationTemplatePublishingMenuItem';
import {
  DeletePresentationTemplateMenuItem,
  DuplicatePresentationTemplateMenuItem,
  UpdatePresentationTemplatePinMenuItem,
} from './TenantPresentationTemplateTable.components';
import {
  TemplatePresentationTemplateTableRowData,
  useTenantPresentationTemplateTableData,
} from './TenantPresentationTemplateTable.hooks';

export interface TenantPresentationTemplateTableProps {
  onRowClick: (row: TemplatePresentationTemplateTableRowData) => void;
  variant: PresentationTemplateTableVariant;
  alwaysShowPaginationFooter?: boolean;
  /**
   * When provided, duplicating a row opens the new copy in the editor instead
   * of staying on the list. The user-level (household) page passes this; the
   * admin page omits it so duplicates stay in the list.
   */
  onDuplicateComplete?: (newTemplateId: string) => void;
}

function TenantTemplateTableEmptyState() {
  return <TableEmptyState text="No firm-level templates have been created" />;
}

function TenantTemplateTableEmptyStateWithCreate() {
  const { navigate } = useNavigateToRoute();

  const handleCreate = useCallback(() => {
    navigate(ROUTE_KEYS.ADMIN_PRESENTATION_TEMPLATE_EDITOR, {
      templateId: NEW_TEMPLATE_SENTINEL,
    });
  }, [navigate]);

  return (
    <TableEmptyState text="No firm-level templates have been created">
      <Button variant="primary" size="sm" onClick={handleCreate}>
        Create a new template
      </Button>
    </TableEmptyState>
  );
}

export function TenantPresentationTemplateTable({
  onRowClick,
  variant,
  alwaysShowPaginationFooter = false,
  onDuplicateComplete,
}: TenantPresentationTemplateTableProps) {
  const { rows, loading, error, refetch, isOrganizationParent } =
    useTenantPresentationTemplateTableData();

  useReportError('failed to load tenant presentation templates', error);

  const isTenantLevel = variant === PresentationTemplateTableVariant.Tenant;
  const slots = useMemo(
    () => ({
      noRowsOverlay: isTenantLevel
        ? TenantTemplateTableEmptyStateWithCreate
        : TenantTemplateTableEmptyState,
    }),
    [isTenantLevel]
  );

  const columns = useColumns({
    refetch,
    variant,
    onDuplicateComplete,
    showSharing: isOrganizationParent,
  });

  return (
    <DataTable
      rows={rows}
      columns={columns}
      loading={loading}
      onRowClick={(params) => onRowClick(params.row)}
      slots={slots}
      pageSize={25}
      alwaysShowPaginationFooter={alwaysShowPaginationFooter}
      hideFooterPagination={!alwaysShowPaginationFooter}
    />
  );
}

const SHARING_COLUMN: Column<TemplatePresentationTemplateTableRowData> = {
  field: 'isPublished',
  headerName: 'Sharing',
  valueGetter: (_value, row) =>
    row.isPublished ? 'Published to organization' : 'Private',
  flex: 1,
};

interface UseColumnsOptions {
  refetch: () => void;
  variant: PresentationTemplateTableVariant;
  onDuplicateComplete?: (newTemplateId: string) => void;
  /**
   * Only organization parents can publish templates, so the sharing state is
   * not useful to other tenants.
   */
  showSharing: boolean;
}

function useColumns({
  refetch,
  variant,
  onDuplicateComplete,
  showSharing,
}: UseColumnsOptions): Column<TemplatePresentationTemplateTableRowData>[] {
  return useMemo(() => {
    const duplicateType = presentationTemplateTypeByTableVariant[variant];
    const isTenantLevel = variant === PresentationTemplateTableVariant.Tenant;
    const showPinning = isTenantLevel;

    return [
      {
        field: 'displayName',
        headerName: 'Name',
        flex: 1,
      },
      {
        field: 'createdBy',
        headerName: 'Created by',
        flex: 1,
      },
      ...(showSharing ? [SHARING_COLUMN] : []),
      {
        field: 'createdAt',
        headerName: 'Date created',
        flex: 1,
      },
      {
        field: 'updatedAt',
        headerName: 'Last modified',
        flex: 1,
      },
      ...(showPinning
        ? [
            createPinnedPresentationTemplateColumn<TemplatePresentationTemplateTableRowData>(),
          ]
        : []),
      {
        field: 'actions',
        headerName: '',
        renderCell: ActionButtonRenderer({
          actions: ({
            row: { id, isPinned, type, isOwned, isPublished, canPublish },
          }) => [
            ...(showPinning
              ? [
                  {
                    MenuItem: () => (
                      <UpdatePresentationTemplatePinMenuItem
                        presentationTemplateId={id}
                        isPinned={isPinned}
                      />
                    ),
                  },
                ]
              : []),
            ...(isTenantLevel && canPublish
              ? [
                  {
                    MenuItem: () => (
                      <PresentationTemplatePublishingMenuItem
                        presentationTemplateId={id}
                        action={isPublished ? 'unpublish' : 'publish'}
                      />
                    ),
                  },
                ]
              : []),
            {
              MenuItem: () => (
                <DuplicatePresentationTemplateMenuItem
                  presentationTemplateId={id}
                  duplicateType={duplicateType}
                  onDuplicateComplete={onDuplicateComplete}
                  label={
                    !isOwned && isTenantLevel
                      ? 'Duplicate to my firm'
                      : 'Duplicate'
                  }
                />
              ),
            },
            ...(isTenantLevel &&
            isOwned &&
            type === PresentationTemplateType.TenantTemplate
              ? [
                  {
                    MenuItem: () => (
                      <DeletePresentationTemplateMenuItem
                        presentationTemplateId={id}
                        onAction={refetch}
                      />
                    ),
                  },
                ]
              : []),
          ],
        }),
      },
    ];
  }, [onDuplicateComplete, refetch, showSharing, variant]);
}
